import { useState, useEffect, useMemo } from "react";
import { Check, Download, Clock, X, Users, UserCheck, UserX } from "lucide-react";
import { teacherAPI } from "../../../api/aulasApi";
import "./ListStudentsAcompanhar.css";
import SearchInput from "../../Search/Search";

// TODO: Quando alterar o status do aluno, atualizar o status na lista principal de alunos
// TODO: Adicionar um botão para exportar os dados em CSV
// TODO: Filtro de alunos status (Presente, Ausente, Atrasado)

const LoadingState = () => (
    <div className="loading-state">
        <p>Carregando lista de alunos...</p>
    </div>
);

const ErrorState = ({ message }) => (
    <div className="error-state">
        <p>Ocorreu um erro ao buscar os dados: {message}</p>
    </div>
);

export default function ListaAlunos() {
    const [activeTab, setActiveTab] = useState("alunos");
    const [allStudents, setAllStudents] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchStudents = async () => {
            try {
                setIsLoading(true);
                const response = await teacherAPI.getStudentProgress();
                const adaptedStudents = response.data.map(student => ({
                    id: student.ra_aluno,
                    nome: student.nome,
                    imagem: student.imagem,
                    status: student.status,
                    porcentagem: parseFloat(student.porcentagem),
                }));
                setAllStudents(adaptedStudents);
                setError(null);
            } catch (err) {
                console.error("Erro ao buscar alunos:", err);
                setError(err.message || "Não foi possível carregar os alunos.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchStudents();
    }, []);

    const updateStudentStatus = (id, newStatus) => {
        setAllStudents(prevStudents =>
            prevStudents.map(student =>
                student.id === id
                    ? {
                        ...student,
                        status: newStatus,
                        porcentagem: newStatus === "Ausente" ? 0 : student.porcentagem,
                    }
                    : student
            )
        );
    };

    const filteredStudents = useMemo(() => {
        if (!searchTerm) {
            return allStudents;
        }
        return allStudents.filter(student =>
            student.nome.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [allStudents, searchTerm]);

    const summaryCards = useMemo(() => {
        const total = allStudents.length;
        const presentes = allStudents.filter(s => s.status === "Presente").length;
        const ausentes = allStudents.filter(s => s.status === "Ausente").length;
        const atrasados = allStudents.filter(s => s.status === "Atrasado").length;

        return [
            { title: "Total", value: total, icon: Users, baseClass: "card-blue" },
            { title: "Presentes", value: presentes, icon: UserCheck, baseClass: "card-green" },
            { title: "Ausentes", value: ausentes, icon: UserX, baseClass: "card-red" },
            { title: "Atrasados", value: atrasados, icon: Clock, baseClass: "card-yellow" },
        ];
    }, [allStudents]);

    const getStatusColor = (status) => {
        switch (status) {
            case "Presente": return "status-presente";
            case "Atrasado": return "status-atrasado";
            case "Ausente": return "status-ausente";
            default: return "status-default";
        }
    };

    const getProgressBarColor = (status) => {
        switch (status) {
            case "Presente":
            case "Atrasado":
                return "progress-presente";
            case "Ausente":
            default:
                return "progress-default";
        }
    };
    
    const handleImageError = (e) => {
        e.target.onerror = null;
        e.target.src = "https://unirecog.gaek.com.br/imagens/avatar_default.png";
    };

    if (isLoading) return <LoadingState />;
    if (error) return <ErrorState message={error} />;

    return (
        <div className="lista-container">
            <div className="lista-header">
                <h2>Lista de alunos</h2>
            </div>

            <div className="tabs-container">
                <div className="tabs-header">
                    <div className="tabs-list">
                        <button
                            className={`tab-button ${activeTab === "alunos" ? "active" : ""}`}
                            onClick={() => setActiveTab("alunos")}
                        >
                            Alunos ({filteredStudents.length})
                        </button>
                        <button
                            className={`tab-button ${activeTab === "controle" ? "active" : ""}`}
                            onClick={() => setActiveTab("controle")}
                        >
                            Controle de presença
                        </button>
                    </div>
                </div>

                <div
                    className="filter-container"
                    style={{ display: activeTab === "alunos" ? "none" : "flex" }}
                >
                    <div className="filter-item">
                        <SearchInput
                            placeholder="Procure por aluno..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <div className="export-button-container">
                            <button className="export-button">
                                <Download className="icon-download" />
                                Exportar
                            </button>
                        </div>
                    </div>
                </div>

                <div className="dashboard-container" style={{ display: activeTab === "alunos" ? "none" : "flex" }}>
                    {summaryCards.map((card, index) => {
                        const IconComponent = card.icon;
                        return (
                            <div key={index} className={`dashboard-card ${card.baseClass}`}>
                                <div className="card-header">
                                    <div className={`icon-container ${card.baseClass}-icon-bg`}>
                                        <IconComponent size={16} className={`icon ${card.baseClass}-icon`} />
                                    </div>
                                    <span className={`card-title ${card.baseClass}-text`}>{card.title}</span>
                                </div>
                                <div className={`card-value ${card.baseClass}-text`}>{card.value}</div>
                            </div>
                        );
                    })}
                </div>

                <div className="content-scroll-area">
                    <div className="tab-content" style={{ display: activeTab === "alunos" ? "block" : "none" }}>
                        <ul className="alunos-list">
                            {filteredStudents.map((aluno) => (
                                <li key={aluno.id} className="aluno-item">
                                    <div className="aluno-avatar">
                                        <img
                                            src={aluno.imagem}
                                            alt={aluno.nome}
                                            onError={handleImageError}
                                            width={40} height={40}
                                            className="rounded-avatar"
                                        />
                                    </div>
                                    <div className="aluno-info">
                                        <div className="aluno-header">
                                            <span className="aluno-nome">{aluno.nome}</span>
                                            <span className={`status-badge ${getStatusColor(aluno.status)}`}>
                                                {aluno.status}
                                            </span>
                                        </div>
                                        <div className="progress-container">
                                            <div className="progress-bar">
                                                <div
                                                    className={`progress-fill ${getProgressBarColor(aluno.status)}`}
                                                    style={{ width: `${aluno.porcentagem}%` }}
                                                ></div>
                                            </div>
                                            <div className="progress-percent">{aluno.porcentagem}%</div>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="tab-content" style={{ display: activeTab === "controle" ? "block" : "none" }}>
                        <ul className="alunos-list">
                            {filteredStudents.map((aluno) => (
                                <li key={aluno.id} className="aluno-item">
                                    <div className="aluno-avatar">
                                        <img
                                            src={aluno.imagem}
                                            alt={aluno.nome}
                                            onError={handleImageError}
                                            width={40} height={40}
                                            className="rounded-avatar"
                                        />
                                    </div>
                                    <div className="aluno-info">
                                        <div className="aluno-header">
                                            <span className="aluno-nome">{aluno.nome}</span>
                                            <div className="status-buttons">
                                                {["Presente", "Atrasado", "Ausente"].map(status => {
                                                    const icons = { Presente: Check, Atrasado: Clock, Ausente: X };
                                                    const Icon = icons[status];
                                                    return (
                                                        <button
                                                            key={status}
                                                            onClick={() => updateStudentStatus(aluno.id, status)}
                                                            className={`status-button ${aluno.status === status ? `${status.toLowerCase()}-active` : ""}`}
                                                            aria-label={`Marcar como ${status.toLowerCase()}`}
                                                        >
                                                            <Icon size={16} />
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                        <div className="progress-container">
                                            <div className="progress-bar">
                                                <div
                                                    className={`progress-fill ${getProgressBarColor(aluno.status)}`}
                                                    style={{ width: `${aluno.porcentagem}%` }}
                                                ></div>
                                            </div>
                                            <div className="progress-percent">{aluno.porcentagem}%</div>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}